/**
 * 分拣动态推荐算法
 * 基于实时扫描的唛头和序号，动态推荐合适的托盘
 */

import { SortingDetail, PalletUsage, PalletUsageDetail, AlgorithmConfig, DynamicRecommendationResult, Pallet } from '../types';

// 模拟数据库存储
class MockDatabase {
  private sortingDetails: SortingDetail[] = [];
  private palletUsages: PalletUsage[] = [];
  private palletUsageDetails: PalletUsageDetail[] = [];
  private sortingPlanStatus: Map<string, 'pending' | 'in_progress' | 'completed' | 'cancelled'> = new Map();
  private pallets: Pallet[] = []; // 存储托盘信息，用于获取预计件数
  private palletNoMapping: Map<string, string> = new Map(); // 原始托盘编号 -> 实时托盘编号映射
  private realtimePalletCounter: number = 1; // 实时托盘编号计数器

  /**
   * 初始化数据
   * @param details 分拣明细
   * @param pallets 托盘信息（用于获取预计件数）
   */
  init(details: SortingDetail[], pallets?: Pallet[]): void {
    this.sortingDetails = [...details];
    this.palletUsages = [];
    this.palletUsageDetails = [];
    this.sortingPlanStatus.clear();
    this.pallets = pallets || [];
    this.palletNoMapping.clear();
    this.realtimePalletCounter = 1;

    // 初始化分拣计划状态
    const planNos = [...new Set(details.map(d => d.palletNo?.split('-')[0] || 'DEFAULT'))];
    planNos.forEach(planNo => {
      this.sortingPlanStatus.set(planNo, 'pending');
    });
  }

  /**
   * 获取或创建实时托盘编号
   * @param originalPalletNo 原始托盘编号
   * @returns 实时托盘编号
   */
  getRealtimePalletNo(originalPalletNo: string): string {
    if (!this.palletNoMapping.has(originalPalletNo)) {
      const realtimeNo = `PALLET-${this.realtimePalletCounter++}`;
      this.palletNoMapping.set(originalPalletNo, realtimeNo);
    }
    return this.palletNoMapping.get(originalPalletNo)!;
  }

  /**
   * 根据实时托盘编号获取原始托盘编号
   * @param realtimePalletNo 实时托盘编号
   * @returns 原始托盘编号
   */
  getOriginalPalletNo(realtimePalletNo: string): string | undefined {
    for (const [original, realtime] of this.palletNoMapping.entries()) {
      if (realtime === realtimePalletNo) {
        return original;
      }
    }
    return undefined;
  }

  /**
   * 获取托盘的预计件数
   * @param palletNo 托盘编号
   * @returns 预计件数
   */
  getPalletEstimatedQuantity(palletNo: string): number {
    const pallet = this.pallets.find(p => p.palletNo === palletNo);
    return pallet?.totalQuantity || 1;
  }

  /**
   * 查询分拣明细
   * @param markPrefix 唛头前缀
   * @param markSerialNo 唛头序号
   * @returns 分拣明细列表
   */
  querySortingDetails(markPrefix: string, markSerialNo: string): SortingDetail[] {
    if (markSerialNo) {
      // 有序号时，精确匹配前缀和序号
      return this.sortingDetails.filter(detail => 
        detail.markPrefix === markPrefix && detail.markSerialNo === markSerialNo
      );
    } else {
      // 无序号时，只匹配前缀
      return this.sortingDetails.filter(detail => 
        detail.markPrefix === markPrefix
      );
    }
  }

  /**
   * 查询分拣计划状态
   * @param planNo 分拣计划编号
   * @returns 分拣计划状态
   */
  querySortingPlanStatus(planNo: string): 'pending' | 'in_progress' | 'completed' | 'cancelled' {
    return this.sortingPlanStatus.get(planNo) || 'pending';
  }

  /**
   * 更新分拣计划状态
   * @param planNo 分拣计划编号
   * @param status 新状态
   */
  updateSortingPlanStatus(planNo: string, status: 'pending' | 'in_progress' | 'completed' | 'cancelled'): void {
    this.sortingPlanStatus.set(planNo, status);
  }

  /**
   * 查询托盘使用情况
   * @param markPrefix 唛头前缀
   * @returns 托盘使用明细列表
   */
  queryPalletUsageDetails(markPrefix: string): PalletUsageDetail[] {
    // 首先查询包含该唛头的明细
    const relatedDetails = this.sortingDetails.filter(detail => detail.markPrefix === markPrefix);
    const detailIds = relatedDetails.map(detail => detail.id);

    // 然后查询这些明细的托盘使用情况
    return this.palletUsageDetails.filter(detail => 
      detailIds.includes(detail.sortingDetailNo)
    );
  }

  /**
   * 查询使用中的托盘
   * @param palletNos 托盘编号列表
   * @returns 使用中的托盘列表
   */
  queryInUsePallets(palletNos: string[]): PalletUsage[] {
    return this.palletUsages.filter(usage => 
      palletNos.includes(usage.palletNo) && usage.status === 'in_use'
    );
  }

  /**
   * 根据托盘编号查询托盘使用状态
   * @param palletNo 托盘编号（可以是原始编号或实时编号）
   * @returns 托盘使用状态
   */
  queryPalletUsageByNo(palletNo: string): PalletUsage | undefined {
    // 先直接查找
    let usage = this.palletUsages.find(usage => usage.palletNo === palletNo);
    if (usage) return usage;
    
    // 如果没找到，尝试通过映射查找
    const realtimeNo = this.palletNoMapping.get(palletNo);
    if (realtimeNo) {
      return this.palletUsages.find(usage => usage.palletNo === realtimeNo);
    }
    
    return undefined;
  }

  /**
   * 创建新托盘
   * @param palletNo 托盘编号
   * @param volume 初始体积
   * @param weight 初始重量
   * @param status 状态
   * @param estimatedQuantity 预计件数
   */
  createPallet(palletNo: string, volume: number, weight: number, status: 'in_use' | 'completed', estimatedQuantity?: number): void {
    this.palletUsages.push({
      palletNo,
      currentWeight: weight,
      currentVolume: volume,
      status,
      actualQuantity: 1,
      estimatedQuantity: estimatedQuantity || this.getPalletEstimatedQuantity(palletNo),
      loadedDetails: []
    });
  }

  /**
   * 更新托盘使用状态
   * @param palletNo 托盘编号
   * @param volume 增加的体积
   * @param weight 增加的重量
   * @param detail 分拣明细
   */
  updatePalletUsage(palletNo: string, volume: number, weight: number, detail: SortingDetail): void {
    const pallet = this.palletUsages.find(usage => usage.palletNo === palletNo);
    if (pallet) {
      pallet.currentVolume += volume;
      pallet.currentWeight += weight;
      pallet.actualQuantity += 1;
      pallet.loadedDetails.push(detail);
      // 更新预计件数：使用明细所属托盘的预计件数
      if (detail.palletNo) {
        const estimatedQty = this.getPalletEstimatedQuantity(detail.palletNo);
        if (estimatedQty > 1) {
          pallet.estimatedQuantity = estimatedQty;
        }
      }
    }
  }

  /**
   * 添加托盘使用明细
   * @param palletNo 托盘编号
   * @param sortingDetailNo 分拣明细编号
   */
  addPalletUsageDetail(palletNo: string, sortingDetailNo: string): void {
    this.palletUsageDetails.push({
      palletNo,
      sortingDetailNo
    });
  }

  /**
   * 更新分拣明细状态
   * @param detailId 明细ID
   * @param status 新状态
   */
  updateSortingDetailStatus(detailId: string, status: 'pending' | 'completed'): void {
    const detail = this.sortingDetails.find(d => d.id === detailId);
    if (detail) {
      detail.status = status;
    }
  }

  /**
   * 获取所有托盘使用情况
   * @returns 托盘使用列表
   */
  getAllPalletUsages(): PalletUsage[] {
    return [...this.palletUsages];
  }

  /**
   * 获取所有托盘使用明细
   * @returns 托盘使用明细列表
   */
  getAllPalletUsageDetails(): PalletUsageDetail[] {
    return [...this.palletUsageDetails];
  }

  /**
   * 查询相同客户的使用中托盘
   * @param customer 客户名称
   * @returns 使用中的托盘列表
   */
  queryInUsePalletsByCustomer(customer: string): PalletUsage[] {
    return this.palletUsages.filter(usage => {
      if (usage.status !== 'in_use') return false;
      // 检查托盘中的货物是否属于该客户
      return usage.loadedDetails.some(detail => detail.customer === customer);
    });
  }
}

// 全局数据库实例
const db = new MockDatabase();

// 托盘编号计数器
let palletCounter = 1;

/**
 * 生成唯一托盘编号
 * @returns 托盘编号
 */
function generatePalletNo(): string {
  return `PALLET-${palletCounter++}`;
}

/**
 * 分拣动态推荐算法实现
 * @param markPrefix 唛头前缀
 * @param markSerialNo 唛头序号
 * @param config 分拣配置
 * @returns 动态推荐结果
 */
export function recommendPalletDynamically(
  markPrefix: string, 
  markSerialNo: string, 
  config: AlgorithmConfig
): DynamicRecommendationResult {
  // 1. 查询分拣明细
  let details: SortingDetail[];
  
  if (markSerialNo) {
    // 序号解析成功：使用唛头前缀+序号查询
    details = db.querySortingDetails(markPrefix, markSerialNo);
    
    // 如果未查询到明细，尝试使用唛头前缀查询（序号解析失败的情况）
    if (details.length === 0) {
      details = db.querySortingDetails(markPrefix, '');
    }
  } else {
    // 序号解析失败：使用唛头前缀查询
    details = db.querySortingDetails(markPrefix, '');
  }

  // 2. 未查询到明细，返回分拣失败(多货)
  if (details.length === 0) {
    return {
      success: false,
      error: '未查询到分拣明细，分拣失败(多货)'
    };
  }

  // 3. 准备明细数据，添加分拣计划状态
  const detailsWithPlanStatus = details.map(detail => {
    const planNo = detail.palletNo?.split('-')[0] || 'DEFAULT';
    const planStatus = db.querySortingPlanStatus(planNo);
    return {
      detail,
      planStatus,
      value: detail.weight * detail.volume // 重量*体积乘积
    };
  });

  // 4. 按照优先级排序明细
  detailsWithPlanStatus.sort((a, b) => {
    // 优先级顺序：
    // 1. 分拣明细=待分拣，分拣计划=分拣中
    // 2. 分拣明细=待分拣，分拣计划=待分拣
    // 3. 分拣明细=已分拣，分拣计划=分拣中
    // 4. 分拣明细=已分拣，分拣计划=已完成
    
    const getPriority = (item: any): number => {
      if (item.detail.status === 'pending') {
        if (item.planStatus === 'in_progress') return 1;
        if (item.planStatus === 'pending') return 2;
      } else if (item.detail.status === 'completed') {
        if (item.planStatus === 'in_progress') return 3;
        if (item.planStatus === 'completed') return 4;
      }
      return 5; // 其他情况
    };
    
    const priorityA = getPriority(a);
    const priorityB = getPriority(b);
    
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // 优先级相同，按重量*体积乘积升序
    return a.value - b.value;
  });

  // 5. 选择优先级最高的明细
  const selectedItem = detailsWithPlanStatus[0];
  const selectedDetail = selectedItem.detail;

  // 获取托盘的预计件数
  const estimatedQuantity = db.getPalletEstimatedQuantity(selectedDetail.palletNo || '');

  // 6. 如果明细状态是已分拣，返回重复扫描
  if (selectedDetail.status === 'completed') {
    return {
      success: false,
      error: '重复扫描',
      palletNo: selectedDetail.palletNo,
      palletInfo: {
        usedQuantity: 1,
        estimatedQuantity: estimatedQuantity,
        usedVolume: selectedDetail.volume,
        usedWeight: selectedDetail.weight
      }
    };
  }

  // 7. 【优先逻辑】检查货物原本所属的托盘是否已经在使用中
  const originalPalletNo = selectedDetail.palletNo;
  if (originalPalletNo) {
    // 获取实时托盘编号
    const realtimePalletNo = db.getRealtimePalletNo(originalPalletNo);
    const originalPalletUsage = db.queryPalletUsageByNo(originalPalletNo);
    
    if (originalPalletUsage && originalPalletUsage.status === 'in_use') {
      // 7.1 原本所属的托盘正在使用中，检查是否能容纳
      const newVolume = originalPalletUsage.currentVolume + selectedDetail.volume;
      const newWeight = originalPalletUsage.currentWeight + selectedDetail.weight;

      if (newVolume <= config.palletVolumeLimit && newWeight <= config.palletWeightLimit) {
        // 7.1.1 能容纳，更新托盘使用状态
        // 注意：updatePalletUsage 会更新 actualQuantity，所以先记录当前值
        const currentUsedQuantity = originalPalletUsage.actualQuantity;
        db.updatePalletUsage(originalPalletUsage.palletNo, selectedDetail.volume, selectedDetail.weight, selectedDetail);
        db.addPalletUsageDetail(originalPalletUsage.palletNo, selectedDetail.id);
        db.updateSortingDetailStatus(selectedDetail.id, 'completed');

        // 更新分拣计划状态为分拣中
        const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
        db.updateSortingPlanStatus(planNo, 'in_progress');

        return {
          success: true,
          palletNo: realtimePalletNo, // 返回实时托盘编号
          message: '正常扫描',
          palletInfo: {
            usedQuantity: currentUsedQuantity + 1,
            estimatedQuantity: estimatedQuantity,
            usedVolume: newVolume,
            usedWeight: newWeight
          }
        };
      }
      // 7.1.2 不能容纳，继续后续逻辑（创建新托盘）
    } else if (!originalPalletUsage) {
      // 7.2 原本所属的托盘还未创建使用记录
      // 【新增】先检查相同客户的使用中托盘（实操优先级）
      const customerInUsePallets = db.queryInUsePalletsByCustomer(selectedDetail.customer);
      
      if (customerInUsePallets.length > 0) {
        // 7.2.1 存在相同客户的使用中托盘，优先使用
        for (const pallet of customerInUsePallets) {
          const newVolume = pallet.currentVolume + selectedDetail.volume;
          const newWeight = pallet.currentWeight + selectedDetail.weight;

          if (newVolume <= config.palletVolumeLimit && newWeight <= config.palletWeightLimit) {
            // 能容纳，更新托盘使用状态
            const currentUsedQuantity = pallet.actualQuantity;
            db.updatePalletUsage(pallet.palletNo, selectedDetail.volume, selectedDetail.weight, selectedDetail);
            db.addPalletUsageDetail(pallet.palletNo, selectedDetail.id);
            db.updateSortingDetailStatus(selectedDetail.id, 'completed');

            // 更新分拣计划状态为分拣中
            const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
            db.updateSortingPlanStatus(planNo, 'in_progress');

            return {
              success: true,
              palletNo: pallet.palletNo,
              message: '相同客户托盘',
              palletInfo: {
                usedQuantity: currentUsedQuantity + 1,
                estimatedQuantity: estimatedQuantity,
                usedVolume: newVolume,
                usedWeight: newWeight
              }
            };
          }
        }
        // 相同客户的托盘都不能容纳，继续创建新托盘
      }
      
      // 7.2.2 创建新的实时托盘（使用实时托盘编号）
      const status = (selectedDetail.volume > config.palletVolumeLimit || selectedDetail.weight > config.palletWeightLimit)
        ? 'completed' : 'in_use';

      db.createPallet(realtimePalletNo, selectedDetail.volume, selectedDetail.weight, status, estimatedQuantity);
      // 获取刚创建的托盘并添加货物明细
      const pallets = db.getAllPalletUsages();
      const newPallet = pallets.find(p => p.palletNo === realtimePalletNo);
      if (newPallet) {
        newPallet.loadedDetails.push(selectedDetail);
      }
      db.addPalletUsageDetail(realtimePalletNo, selectedDetail.id);
      db.updateSortingDetailStatus(selectedDetail.id, 'completed');

      // 更新分拣计划状态为分拣中
      const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
      db.updateSortingPlanStatus(planNo, 'in_progress');

      return {
        success: true,
        palletNo: realtimePalletNo, // 返回实时托盘编号
        message: '创建新托盘',
        palletInfo: {
          usedQuantity: 1,
          estimatedQuantity: estimatedQuantity,
          usedVolume: selectedDetail.volume,
          usedWeight: selectedDetail.weight
        }
      };
    }
  }

  // 8. 【新增逻辑】检查相同客户的使用中托盘（实操优先级：相同客户的货物放同一个托盘）
  const customerInUsePallets = db.queryInUsePalletsByCustomer(selectedDetail.customer);
  
  if (customerInUsePallets.length > 0) {
    // 8.0.1 存在相同客户的使用中托盘，优先使用
    for (const pallet of customerInUsePallets) {
      const newVolume = pallet.currentVolume + selectedDetail.volume;
      const newWeight = pallet.currentWeight + selectedDetail.weight;

      if (newVolume <= config.palletVolumeLimit && newWeight <= config.palletWeightLimit) {
        // 能容纳，更新托盘使用状态
        // 注意：updatePalletUsage 会更新 actualQuantity，所以先记录当前值
        const currentUsedQuantity = pallet.actualQuantity;
        db.updatePalletUsage(pallet.palletNo, selectedDetail.volume, selectedDetail.weight, selectedDetail);
        db.addPalletUsageDetail(pallet.palletNo, selectedDetail.id);
        db.updateSortingDetailStatus(selectedDetail.id, 'completed');

        // 更新分拣计划状态为分拣中
        const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
        db.updateSortingPlanStatus(planNo, 'in_progress');

        return {
          success: true,
          palletNo: pallet.palletNo,
          message: '相同客户托盘',
          palletInfo: {
            usedQuantity: currentUsedQuantity + 1,
            estimatedQuantity: estimatedQuantity,
            usedVolume: newVolume,
            usedWeight: newWeight
          }
        };
      }
    }
    // 8.0.2 相同客户的托盘都不能容纳，继续后续逻辑
  }

  // 9. 【兜底逻辑】查询相同唛头的托盘使用情况（原有逻辑）
  const usageDetails = db.queryPalletUsageDetails(markPrefix);

  if (usageDetails.length > 0) {
    // 9.1 存在托盘使用记录
    const palletNos = [...new Set(usageDetails.map(detail => detail.palletNo))];
    const inUsePallets = db.queryInUsePallets(palletNos);

    if (inUsePallets.length > 0) {
      // 9.1.1 存在使用中的托盘
      for (const pallet of inUsePallets) {
        const newVolume = pallet.currentVolume + selectedDetail.volume;
        const newWeight = pallet.currentWeight + selectedDetail.weight;

        if (newVolume <= config.palletVolumeLimit && newWeight <= config.palletWeightLimit) {
          // 能容纳，更新托盘使用状态
          // 注意：updatePalletUsage 会更新 actualQuantity，所以先记录当前值
          const currentUsedQuantity = pallet.actualQuantity;
          db.updatePalletUsage(pallet.palletNo, selectedDetail.volume, selectedDetail.weight, selectedDetail);
          db.addPalletUsageDetail(pallet.palletNo, selectedDetail.id);
          db.updateSortingDetailStatus(selectedDetail.id, 'completed');

          // 更新分拣计划状态为分拣中
          const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
          db.updateSortingPlanStatus(planNo, 'in_progress');

          return {
            success: true,
            palletNo: pallet.palletNo,
            message: '正常扫描',
            palletInfo: {
              usedQuantity: currentUsedQuantity + 1,
              estimatedQuantity: estimatedQuantity,
              usedVolume: newVolume,
              usedWeight: newWeight
            }
          };
        }
      }

      // 9.1.2 不能容纳，创建新托盘
      const newPalletNo = generatePalletNo();
      const status = (selectedDetail.volume > config.palletVolumeLimit || selectedDetail.weight > config.palletWeightLimit)
        ? 'completed' : 'in_use';

      db.createPallet(newPalletNo, selectedDetail.volume, selectedDetail.weight, status, estimatedQuantity);
      // 获取刚创建的托盘并添加货物明细
      const pallets = db.getAllPalletUsages();
      const newPallet = pallets.find(p => p.palletNo === newPalletNo);
      if (newPallet) {
        newPallet.loadedDetails.push(selectedDetail);
      }
      db.addPalletUsageDetail(newPalletNo, selectedDetail.id);
      db.updateSortingDetailStatus(selectedDetail.id, 'completed');

      // 更新分拣计划状态为分拣中
      const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
      db.updateSortingPlanStatus(planNo, 'in_progress');

      return {
        success: true,
        palletNo: newPalletNo,
        message: '创建新托盘',
        palletInfo: {
          usedQuantity: 1,
          estimatedQuantity: estimatedQuantity,
          usedVolume: selectedDetail.volume,
          usedWeight: selectedDetail.weight
        }
      };
    } else {
      // 9.2 不存在使用中的托盘，创建新托盘
      const newPalletNo = generatePalletNo();
      const status = (selectedDetail.volume > config.palletVolumeLimit || selectedDetail.weight > config.palletWeightLimit)
        ? 'completed' : 'in_use';

      db.createPallet(newPalletNo, selectedDetail.volume, selectedDetail.weight, status, estimatedQuantity);
      // 获取刚创建的托盘并添加货物明细
      const pallets = db.getAllPalletUsages();
      const newPallet = pallets.find(p => p.palletNo === newPalletNo);
      if (newPallet) {
        newPallet.loadedDetails.push(selectedDetail);
      }
      db.addPalletUsageDetail(newPalletNo, selectedDetail.id);
      db.updateSortingDetailStatus(selectedDetail.id, 'completed');

      // 更新分拣计划状态为分拣中
      const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
      db.updateSortingPlanStatus(planNo, 'in_progress');

      return {
        success: true,
        palletNo: newPalletNo,
        message: '创建新托盘',
        palletInfo: {
          usedQuantity: 1,
          estimatedQuantity: estimatedQuantity,
          usedVolume: selectedDetail.volume,
          usedWeight: selectedDetail.weight
        }
      };
    }
  } else {
    // 7. 不存在托盘使用记录，创建新托盘
    const newPalletNo = generatePalletNo();
    const status = (selectedDetail.volume > config.palletVolumeLimit || selectedDetail.weight > config.palletWeightLimit)
      ? 'completed' : 'in_use';

    db.createPallet(newPalletNo, selectedDetail.volume, selectedDetail.weight, status, estimatedQuantity);
    // 获取刚创建的托盘并添加货物明细
    const pallets = db.getAllPalletUsages();
    const newPallet = pallets.find(p => p.palletNo === newPalletNo);
    if (newPallet) {
      newPallet.loadedDetails.push(selectedDetail);
    }
    db.addPalletUsageDetail(newPalletNo, selectedDetail.id);
    db.updateSortingDetailStatus(selectedDetail.id, 'completed');

    // 更新分拣计划状态为分拣中
    const planNo = selectedDetail.palletNo?.split('-')[0] || 'DEFAULT';
    db.updateSortingPlanStatus(planNo, 'in_progress');

    return {
      success: true,
      palletNo: newPalletNo,
      message: '创建新托盘',
      palletInfo: {
        usedQuantity: 1,
        estimatedQuantity: estimatedQuantity,
        usedVolume: selectedDetail.volume,
        usedWeight: selectedDetail.weight
      }
    };
  }
}

/**
 * 初始化数据库
 * @param details 分拣明细列表
 * @param pallets 托盘信息列表（用于获取预计件数）
 */
export function initDatabase(details: SortingDetail[], pallets?: Pallet[]): void {
  db.init(details, pallets);
  // 重置托盘编号计数器
  palletCounter = 1;
}

/**
 * 获取当前托盘使用状态
 * @returns 托盘使用状态列表
 */
export function getCurrentPalletUsages(): PalletUsage[] {
  return db.getAllPalletUsages();
}

/**
 * 获取当前托盘使用明细
 * @returns 托盘使用明细列表
 */
export function getCurrentPalletUsageDetails(): PalletUsageDetail[] {
  return db.getAllPalletUsageDetails();
}
